import { useEffect, useRef, useState } from 'react'
import { openModal, closeAllModals } from '@mantine/modals'
import { LoadingOverlay, Global } from '@mantine/core'
import { pick, findIndex } from 'lodash-es'
import clsx from 'clsx'
import { IconShare, IconFileExport } from '@tabler/icons'
import { DetailStoreProvider, useTbViewStore, useDetailStore } from '@vt/hooks'
import { showNotification } from '@mantine/notifications'
import { getQuery } from '@vt/utils'
import { useTheme } from '@ow/theme'
import { Button, Tooltip } from '@ow/comps'
import { CloseIcon, LeftArrowIcon, RightArrowIcon } from '@ow/icons'
import { exportNoticeFile } from '@vt/apis/lib-ex/v2/tables'
import SharePopup from '../../share/sharePopup'
import Content from './Content'
import Edit from './edit'

const Header = ({
  close,
  isShowLog,
  onCloseOrOpenLog,
  onClickNextOrPrev,
  onLoading,
  onClickSubmit,
  allowShare,
  allowEdit,
  allowProblemExport,
  preview,
}) => {
  const { tbData, ditto, viewType, detailConfig, allowDetailOperate } =
    useTbViewStore((state) =>
      pick(state, [
        'tbData',
        'ditto',
        'viewType',
        'detailConfig',
        'allowDetailOperate',
      ])
    )
  const { currentData, editableSlugs } = useDetailStore((state) =>
    pick(state, ['currentData', 'editableSlugs'])
  )

  // 是否显示操作日志
  const allowLog =
    detailConfig && detailConfig.allowLog && detailConfig.allowLog.allow

  const isEditing = editableSlugs.length > 0

  const currentDataIndex =
    currentData &&
    findIndex(
      tbData,
      (v) => v[ditto.idFiledName] === currentData[ditto.idFiledName]
    )

  const getExportlUrl = (url) => {
    if (url.startsWith('https')) return url
    return `https${url.substring(4)}`
  }

  const exportNotificationFile = async () => {
    // 导出生成问题通知单，丰台定制接口
    const { tableDataId = '' } = currentData || {}
    if (!tableDataId) return
    const res = await exportNoticeFile(tableDataId)
    if (!res || res.length === 0) return
    const a = document.createElement('a')
    a.href = getExportlUrl(res[0].data)
    a.click()
  }

  return (
    <div
      className="w-full flex items-center h-14 px-4 justify-between"
      style={{
        fontSize: '1rem',
        fontWeight: 'bold',
        borderBottom: '1px solid #E7E7E7',
        color: 'rgba(0,0,0,0.90)',
      }}
    >
      <span>查看详情</span>

      {!preview && (
        <div className="flex items-center justify-end gap-3">
          {allowEdit && allowDetailOperate !== false && (
            <Edit isEditing={isEditing} onClickSubmit={onClickSubmit} />
          )}
          {allowLog && allowDetailOperate !== false && (
            <Button onClick={onCloseOrOpenLog} stroke>
              {isShowLog ? '收起日志' : '展开日志'}
            </Button>
          )}

          {!isEditing && tbData && tbData.length > 0 && viewType !== 'matrix' && (
            <div className="flex items-center gap-3">
              {currentDataIndex > 0 && (
                <Tooltip
                  className="bg-primary-50"
                  trigger={
                    <span style={{ marginRight: '10px' }}>
                      <Button
                        type="secondary"
                        style={{ padding: '0.5rem' }}
                        opacityType="sm"
                        onClick={() => {
                          onClickNextOrPrev(tbData[currentDataIndex - 1])
                        }}
                      >
                        <LeftArrowIcon fill="currentColor" />
                      </Button>
                    </span>
                  }
                  position="bottom"
                  on={['hover', 'focus']}
                  arrow="top"
                  style={{ background: '#F3F3F3', color: '#191919' }}
                  arrowStyle={{ color: '#F3F3F3' }}
                >
                  上一条
                </Tooltip>
              )}
              {currentDataIndex + 1 < tbData.length && viewType !== 'matrix' && (
                <Tooltip
                  className="bg-primary-50"
                  trigger={
                    <span style={{ marginRight: '10px' }}>
                      <Button
                        type="secondary"
                        style={{ padding: '0.5rem' }}
                        opacityType="sm"
                        onClick={() => {
                          onClickNextOrPrev(tbData[currentDataIndex + 1])
                        }}
                      >
                        <RightArrowIcon fill="currentColor" />
                      </Button>
                    </span>
                  }
                  position="bottom"
                  on={['hover', 'focus']}
                  arrow="top"
                  style={{ background: '#F3F3F3', color: '#191919' }}
                  arrowStyle={{ color: '#F3F3F3' }}
                >
                  下一条
                </Tooltip>
              )}
            </div>
          )}
          {allowShare && viewType !== 'matrix' && allowDetailOperate !== false && (
            <SharePopup objectId={currentData?.objectId} onLoading={onLoading}>
              <div className="cursor-pointer z-10">
                <IconShare size={18} />
              </div>
            </SharePopup>
          )}
          {allowProblemExport && allowDetailOperate !== false && (
            <IconFileExport
              className="cursor-pointer hover:opacity-60"
              stroke="grey"
              onClick={exportNotificationFile}
            />
          )}
          <div className="cursor-pointer z-10" onClick={close}>
            <CloseIcon width={18} height={18} fill="currentColor" />
          </div>
        </div>
      )}
    </div>
  )
}

const openDetailModal = ({ currentData }) => {
  openModal({
    modalId: currentData.objectId,
    children: <PureDetail currentData={currentData} />,
  })
}

const ModalContent = ({
  showData,
  detailsComponent: DetailsComponent,
  openUpdateModal,
}) => {
  const formRef = useRef(null)
  const { themeClass } = useTheme()
  const [loading, setLoading] = useState(false)
  const [currentShowData, setCurrentShowData] = useState(null)
  const [isShowLog, setIsShowLog] = useState(true)

  const {
    tbData,
    detailConfig,
    fetchTbDataById,
    updateTbData,
    customProcessFreshTbDataByIds,
  } = useTbViewStore((state) =>
    pick(state, [
      'tbData',
      'detailConfig',
      'fetchTbDataById',
      'updateTbData',
      'customProcessFreshTbDataByIds',
    ])
  )
  const {
    currentData,
    setOriginalData,
    setCurrentData,
    reset: resetDetailStoreData,
    setEditableSlugs,
    setEditableSlugFields,
  } = useDetailStore((state) =>
    pick(state, [
      'currentData',
      'setOriginalData',
      'setCurrentData',
      'reset',
      'setEditableSlugs',
      'setEditableSlugFields',
    ])
  )

  // 分享详情展示模式
  const { sharedId, oid } = getQuery()
  const isSharedDetailMode = sharedId && oid

  // 点击确定
  const onClickSubmit = (callback, reject) => {
    if (formRef && formRef.current) {
      formRef.current.trigger().then((tr) => {
        console.log('data validate:', tr)
        if (tr) {
          updateTbData(formRef.current.getValues()).then(() => {
            showNotification({
              title: '操作成功',
            })
            callback()
          })
        } else if (reject) {
          reject()
        }
      })
    }
  }

  const onClickSubmitProcess = (data) => {
    return customProcessFreshTbDataByIds([data.objectId])
  }

  const closeModal = () => {
    setEditableSlugs([])
    setEditableSlugFields([])
    resetDetailStoreData()
    closeAllModals()
  }

  const reloadDataInfo = () => {
    setLoading(true)
    fetchTbDataById(currentShowData.objectId)
      .then((res) => {
        if (res.originalData) {
          setOriginalData(res.originalData)
          setCurrentData(res.currentData)
        } else {
          showNotification({
            title: '提示',
            message: '要查看的数据不存在，请确认是否被删除',
          })
          // closeModal()
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (currentShowData && currentShowData.objectId) {
      reloadDataInfo()
    }
  }, [currentShowData])

  useEffect(() => {
    setCurrentShowData(showData)
  }, [showData])

  useEffect(() => {
    // 这里保证是最新的数据
    if (currentShowData) {
      const obj = tbData.find((v) => v.objectId === currentShowData.objectId)

      if (obj) {
        setCurrentShowData(obj)
      } else {
        reloadDataInfo()
      }
    }
  }, [tbData])

  useEffect(() => {
    if (!detailConfig) return
    if ('allowLog' in detailConfig && 'defaultShow' in detailConfig.allowLog) {
      setIsShowLog(detailConfig.allowLog.defaultShow)
    }
  }, [detailConfig])

  return (
    <div
      className={clsx(themeClass, 'flex flex-col relative h-full')}
      style={{
        width: '85vw',
        background: '#fff',
        borderRadius: '4px',
      }}
    >
      <LoadingOverlay visible={loading} />
      <Header
        preview={isSharedDetailMode}
        close={closeModal}
        allowShare={detailConfig?.allowShare && !sharedId}
        allowEdit={detailConfig?.allowEdit && !sharedId}
        allowProblemExport={detailConfig?.allowProblemExport && !sharedId}
        isShowLog={isShowLog}
        onLoading={setLoading}
        onClickSubmit={onClickSubmit}
        onClickNextOrPrev={setCurrentShowData}
        onCloseOrOpenLog={() => {
          setIsShowLog(!isShowLog)
        }}
      />

      {DetailsComponent ? (
        <DetailsComponent
          data={currentData}
          ref={formRef}
          closeModal={closeModal}
        >
          <Content
            ref={formRef}
            isShowLog={isShowLog}
            onClickSubmitProcess={() => {
              return onClickSubmitProcess(currentData)
            }}
            openUpdateModal={openUpdateModal}
          />
        </DetailsComponent>
      ) : (
        <Content
          ref={formRef}
          isShowLog={isShowLog}
          closeModal={closeModal}
          onClickSubmitProcess={() => {
            return onClickSubmitProcess(currentData)
          }}
          openUpdateModal={openUpdateModal}
        />
      )}
    </div>
  )
}

const PureDetail = ({ currentData }) => {
  return (
    <DetailStoreProvider>
      <Global
        styles={() => ({
          '.mantine-Modal-modal': {
            width: 'unset',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
          '.mantine-Modal-header': {
            display: 'none',
          },
          '.mantine-Modal-body': {
            flex: 1,
            height: '100%',
            overflow: 'hidden',
            '.mantine-y7yj5k': {
              overflow: 'hidden',
              height: '100%',
            },
          },
        })}
      />
      <ModalContent showData={currentData} />
    </DetailStoreProvider>
  )
}

// 打开弹框展示详情
export { openDetailModal as showDetail }
// 无弹框展示详情
export default PureDetail
